'use client'

import type { ReactNode } from 'react'
import Image from 'next/image'

import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { AppSidebar } from '@/components/app-sidebar'
import { FooterContainer } from '@/components/footer-container'
import { Navbar, NavbarAuth, type MenuItem, type NavbarLogo } from '@/components/navbar'
import { Link, usePathname } from '@/i18n/navigation'

// Default navigation configuration
// TODO: These should eventually come from a CMS or i18n
const logo: NavbarLogo = {
    url: '/',
    src: '/next-static/logos/chiku-and-wanderu-horizontal.svg',
    width: 192,
    height: 44,
    alt: 'logo',
}

const menu: MenuItem[] = [
    {
        title: 'Buses',
        url: '/en-us/bus-tickets/',
        items: [
            { title: 'Boston to NYC bus', url: '/en-us/bus/us-ma/boston/us-ny/new-york/' },
            { title: 'NYC to Boston bus', url: '/en-us/bus/us-ny/new-york/us-ma/boston/' },
            { title: 'DC to NYC bus', url: '/en-us/bus/us-dc/washington/us-ny/new-york/' },
            { title: 'LA to Vegas bus', url: '/en-us/bus/us-ca/los-angeles/us-nv/las-vegas/' },
            { title: 'Dallas to Houston bus', url: '/en-us/bus/us-tx/dallas/us-tx/houston/' },
            { title: 'Houston to Dallas bus', url: '/en-us/bus/us-tx/houston/us-tx/dallas/' },
            {
                title: 'Baltimore to NYC bus',
                url: '/en-us/bus/us-md/baltimore/us-ny/new-york/',
            },
            { title: 'NYC to Albany bus', url: '/en-us/bus/us-ny/new-york/us-ny/albany/' },
            { title: 'Orlando to Miami bus', url: '/en-us/bus/us-fl/orlando/us-fl/miami/' },
            {
                title: 'Philadelphia to NYC bus',
                url: '/en-us/bus/us-pa/philadelphia/us-ny/new-york/',
            },
        ],
    },
    {
        title: 'Trains',
        url: '/en-us/train-tickets/',
        items: [
            { title: 'Boston to NYC train', url: '/en-us/train/us-ma/boston/us-ny/new-york/' },
            { title: 'NYC to Boston train', url: '/en-us/train/us-ny/new-york/us-ma/boston/' },
            { title: 'DC to NYC train', url: '/en-us/train/us-dc/washington/us-ny/new-york/' },
            { title: 'NYC to DC train', url: '/en-us/train/us-ny/new-york/us-dc/washington/' },
            {
                title: 'San Diego to LA train',
                url: '/en-us/train/us-ca/san-diego/us-ca/los-angeles/',
            },
            {
                title: 'LA to San Diego train',
                url: '/en-us/train/us-ca/los-angeles/us-ca/san-diego/',
            },
            {
                title: 'Seattle to Vancouver train',
                url: '/en-us/train/us-wa/seattle/ca-bc/vancouver/',
            },
            {
                title: 'Vancouver to Seattle train',
                url: '/en-us/train/ca-bc/vancouver/us-wa/seattle/',
            },
            {
                title: 'Philadelphia to NYC train',
                url: '/en-us/train/us-pa/philadelphia/us-ny/new-york/',
            },
            {
                title: 'NYC to Baltimore train',
                url: '/en-us/train/us-ny/new-york/us-md/baltimore/',
            },
            { title: 'Newark to DC train', url: '/en-us/train/us-nj/newark/us-dc/washington/' },
        ],
    },
    {
        title: 'Bus & Train Companies',
        shortTitle: 'Carriers',
        url: '/en-us/carrier/',
        items: [
            { title: 'Amtrak', url: '/en-us/carrier/amtrak-train-146/' },
            { title: 'Acela', url: '/en-us/carrier/acela-182/' },
            { title: 'Greyhound Bus', url: '/en-us/carrier/greyhound-bus-lines-139/' },
            { title: 'FlixBus USA', url: '/en-us/carrier/flixbus-us-1956/' },
            { title: 'Peter Pan Bus', url: '/en-us/carrier/peter-pan-bus-lines-132/' },
            { title: 'Red Coach', url: '/en-us/carrier/red-coach-148/' },
            { title: 'California Zephyr', url: '/en-us/carrier/amtrak-california-zephyr-186/' },
            { title: 'All Companies', url: '/en-us/carrier/' },
        ],
    },
    { title: 'Explore', url: '/en-us/explore/' },
    { title: 'Blog', url: 'https://www.wanderu.com/blog/' },
]

const auth: NavbarAuth = {
    login: {
        title: 'Login',
        url: '/en-us/account/login/',
    },
    signup: {
        title: 'Sign Up',
        url: '/en-us/account/signup/',
    },
    myTrips: {
        title: 'My Trips',
        url: '/en-us/account/my-trips/',
    },
    account: {
        title: 'Account',
        url: '/en-us/account/settings/',
    },
    points: {
        title: 'Points',
        url: '/en-us/account/points/',
    },
}

interface AppLayoutProps {
    children: ReactNode
}

const NAVBAR_HIDDEN_ROUTES = ['/deal-me-a-trip']

export function AppLayout({ children }: AppLayoutProps) {
    const pathname = usePathname()
    const hideNavbar = NAVBAR_HIDDEN_ROUTES.some((route) => pathname.includes(route))

    return (
        <div className="flex min-h-screen w-full flex-col">
            {hideNavbar ? (
                <>
                    <header className="w-full border-b bg-background">
                        <div className="container mx-auto flex h-16 items-center px-4 md:px-6">
                            <Link href="/">
                                <Image
                                    src={logo.src}
                                    width={logo.width}
                                    height={logo.height}
                                    alt={logo.alt}
                                    className="h-8 w-auto"
                                />
                            </Link>
                        </div>
                    </header>
                    <main className="flex w-full flex-1 flex-col">{children}</main>
                </>
            ) : (
                <SidebarProvider defaultOpen={false} className="!min-h-0 flex-1">
                    <AppSidebar menu={menu} auth={auth} />
                    <SidebarInset>
                        <Navbar logo={logo} menu={menu} auth={auth} />
                        <main className="flex w-full flex-col">{children}</main>
                    </SidebarInset>
                </SidebarProvider>
            )}
            <FooterContainer />
        </div>
    )
}
